import glamorous from 'glamorous';
import posed, { PoseGroup } from 'react-pose';
import React from 'react';
import tween from 'popmotion/animations/tween';
import { AbsolutePos, AbsolutePosProps, WishHandler, MountedStateProvider, BrandonGrotesque } from 'module/View/Common';
import { Categories } from 'module/Model/Categories';
import { CategoriesSelector } from 'module/Redux/Categories';
import { connect } from 'react-redux';
import { createPureComponent } from 'module/Common';
import { GridProvider } from './GridProvider';
import { ItemCard } from 'module/View/ItemCard';
import { MainSelector } from 'module/Redux/Main';
import { PostList } from 'module/Model/PostList';
import { PostListItem } from 'module/Model/PostList/PostListItem';
import { Route, RouteType } from 'module/Model/Routes';
import { RouterSelector } from 'module/Redux/Router';
import { Size } from 'module/Model/Size';
import { State } from 'module/Redux';
import { Wish } from 'module/Model/Wish';

type TitleTileProps = {
  tileSize: number;
};

const TitleTile = glamorous.div<TitleTileProps>(p => ({
  background: '#0D094C',
  height: '100%',
  width: '100%',
  color: 'white',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: `${p.tileSize < 250 ? Math.max(1, p.tileSize / 125) : 2.7}rem`,
  fontFamily: BrandonGrotesque.bold,
  fontStyle: 'normal',
  fontWeight: 'normal',
  padding: '2rem',
  lineHeight: 1.2,
}));

const transition = ({ from, to }: any) => tween({ from, to, duration: 300 });

const PoseChild: React.SFC<AbsolutePosProps> = (posed as any)(AbsolutePos)({
  enter: { opacity: 1, zIndex: 100, transition },
  exit: { opacity: 0, zIndex: 50, transition },
});

const FullSpace = glamorous.div({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  opacity: 0,
});

const FadeOut = (posed as any)(FullSpace)({
  init: { opacity: 0 },
  normal: { opacity: 1 },
  exit: { opacity: 0 },
});

const PoseGroupAny = PoseGroup as any;

const PostListHandler = WishHandler.create<PostList>();

type StateProps = {
  categoriesWish: Wish<Categories>;
  touch: boolean;
  requestedRoute: Route | null;
  itemGridIsInExitState: boolean;
  routeIsInExitState: boolean;
};

const mapStateToProps = (s: State, props: OwnProps): StateProps => ({
  categoriesWish: CategoriesSelector.selectAll(s),
  touch: MainSelector.isTouchDetected(s),
  requestedRoute: RouterSelector.selectRequestedMatchingRoute(s),
  itemGridIsInExitState: RouterSelector.itemGridIsInExitState(s),
  routeIsInExitState: RouterSelector.routeIsInExitState(s),
});

type OwnProps = {
  postListWish: Wish<PostList>;
  nameTile?: React.ReactElement<any> | null;
  availableWidth: number;
};

type Props = OwnProps & StateProps;

const ItemsGridComp = createPureComponent<Props>(
  'ItemsGrid',
  ({
    postListWish,
    categoriesWish,
    touch,
    nameTile,
    availableWidth,
    requestedRoute,
    itemGridIsInExitState,
    routeIsInExitState,
  }) => {
    const titleTileHeightRatio = 250 / 350;
    const requestedPostId =
      requestedRoute === null ? null : requestedRoute.type === RouteType.Post ? requestedRoute.id : null;
    return (
      <PostListHandler
        wish={postListWish}
        handleResolved={posts => {
          return (
            <GridProvider
              postList={posts}
              availableWidth={availableWidth}
              touch={touch}
              titleTile={!!nameTile}
              titleTileHeightRatio={titleTileHeightRatio}
              requestedPostId={requestedPostId}
              itemGridIsInExitState={itemGridIsInExitState}
            >
              {(gridParams, poses, imageLoaded, loadingDone, registerRef) => (
                <div style={{ position: 'relative', height: gridParams.height }}>
                  <PoseGroupAny>
                    {[
                      nameTile ? (
                        <PoseChild
                          cardCoords={{
                            left: gridParams.horizontalOrigin,
                            top: gridParams.verticalOrigin,
                            width: gridParams.getWidth(gridParams.numOfCol <= 2 ? 1 : 2),
                            height: gridParams.colSize * titleTileHeightRatio,
                            imageHeight: 0,
                          }}
                          transition={true}
                          key="title"
                        >
                          <MountedStateProvider>
                            {mounted => {
                              const pose = routeIsInExitState ? 'exit' : mounted ? 'normal' : 'init';
                              return (
                                <FadeOut pose={pose}>
                                  <TitleTile tileSize={gridParams.colSize}>{nameTile}</TitleTile>
                                </FadeOut>
                              );
                            }}
                          </MountedStateProvider>
                        </PoseChild>
                      ) : null,
                      ...posts.map((post, index) => {
                        const coord = gridParams.coords[index];
                        if (coord === undefined) {
                          return null;
                        }
                        const postId = PostListItem.getId(post);
                        const pose = poses[index];
                        return (
                          <PoseChild transition={true} cardCoords={coord} key={postId}>
                            <ItemCard
                              isTouch={touch}
                              item={post}
                              cardSize={Size.createFromObject(coord)}
                              imageHeight={coord.imageHeight}
                              categoriesWish={categoriesWish}
                              descriptionUnderRef={ref => registerRef(postId, ref)}
                              annimState={pose}
                              onImageLoaded={() => {
                                imageLoaded(postId);
                              }}
                              onLoadingDone={() => {
                                loadingDone(postId);
                              }}
                            />
                          </PoseChild>
                        );
                      }),
                    ]}
                  </PoseGroupAny>
                </div>
              )}
            </GridProvider>
          );
        }}
      />
    );
  }
);

export const ItemsGrid = connect(mapStateToProps)(ItemsGridComp);



// WEBPACK FOOTER //
// ./src/module/View/ItemsGrid/ItemsGrid.tsx